import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase/config';
import { doc, updateDoc } from 'firebase/firestore';
import {
    User,
    Mail,
    Phone,
    Briefcase,
    DollarSign,
    TrendingUp,
    Save,
    Loader
} from 'lucide-react';

export default function ContractorProfile() {
    const { currentUser, userProfile } = useAuth();
    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState(false);
    
    const [profileData, setProfileData] = useState({
        name: '',
        email: '',
        phone: '',
        company: '',
        phase: 'trainee',
        hourlyRate: 18
    });

    useEffect(() => {
        if (userProfile) {
            setProfileData({
                name: userProfile.name || '',
                email: currentUser.email || '',
                phone: userProfile.phone || '',
                company: userProfile.company || '',
                phase: userProfile.phase || 'trainee',
                hourlyRate: userProfile.hourlyRate || 18
            });
        }
    }, [userProfile, currentUser]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setProfileData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setSuccess(false);

        try {
            await updateDoc(doc(db, 'contractors', currentUser.uid), {
                name: profileData.name,
                phone: profileData.phone,
                company: profileData.company,
                updatedAt: new Date().toISOString()
            });

            setSuccess(true);
            setTimeout(() => setSuccess(false), 3000);
        } catch (error) {
            console.error('Error updating profile:', error);
            alert('Failed to update profile');
        } finally {
            setSaving(false);
        }
    };

    const getPhaseInfo = (phase) => {
        const phases = {
            'shadow': { name: 'Shadow', rate: 0, description: 'Observation phase', color: 'gray' },
            'trainee': { name: 'Trainee', rate: 18, description: 'Learning with supervision', color: 'blue' },
            'junior': { name: 'Junior Tech', rate: 22, description: 'Independent on simple jobs', color: 'purple' },
            'standard': { name: 'Standard Tech', rate: 25, description: 'Fully competent', color: 'green' },
            'experienced': { name: 'Experienced Tech', rate: 27, description: 'Top performer', color: 'yellow' }
        };
        return phases[phase] || phases['trainee'];
    };

    const phaseInfo = getPhaseInfo(profileData.phase);

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">
                        Profile & Settings
                    </h1>
                    <p className="text-gray-600 mt-2">
                        Manage your contractor profile
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Main Profile Form */}
                    <div className="lg:col-span-2">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
                        >
                            <h2 className="text-xl font-bold text-gray-900 mb-6">
                                Personal Information
                            </h2>

                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Full Name
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <User className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input
                                            type="text"
                                            name="name"
                                            value={profileData.name}
                                            onChange={handleChange}
                                            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            required
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Email Address
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Mail className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input
                                            type="email"
                                            name="email"
                                            value={profileData.email}
                                            disabled
                                            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg bg-gray-50 cursor-not-allowed"
                                        />
                                    </div>
                                    <p className="mt-1 text-xs text-gray-500">
                                        Email cannot be changed
                                    </p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Phone Number
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Phone className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input
                                            type="tel"
                                            name="phone"
                                            value={profileData.phone}
                                            onChange={handleChange}
                                            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Company Name (Optional)
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Briefcase className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input
                                            type="text"
                                            name="company"
                                            value={profileData.company}
                                            onChange={handleChange}
                                            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                </div>

                                <div className="flex items-center space-x-4 pt-4">
                                    <button
                                        type="submit"
                                        disabled={saving}
                                        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
                                    >
                                        {saving ? (
                                            <>
                                                <Loader className="w-4 h-4 animate-spin" />
                                                <span>Saving...</span>
                                            </>
                                        ) : (
                                            <>
                                                <Save className="w-4 h-4" />
                                                <span>Save Changes</span>
                                            </>
                                        )}
                                    </button>

                                    {success && (
                                        <span className="text-green-600 text-sm font-medium">
                                            ✓ Profile updated successfully
                                        </span>
                                    )}
                                </div>
                            </form>
                        </motion.div>
                    </div>

                    {/* Sidebar - Current Phase */}
                    <div className="space-y-6">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className={`bg-${phaseInfo.color}-50 border-2 border-${phaseInfo.color}-200 rounded-xl p-6`}
                        >
                            <div className="flex items-center space-x-2 mb-4">
                                <TrendingUp className={`w-5 h-5 text-${phaseInfo.color}-600`} />
                                <h3 className="font-bold text-gray-900">Current Level</h3>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <p className={`text-2xl font-bold text-${phaseInfo.color}-800`}>
                                        {phaseInfo.name}
                                    </p>
                                    <p className="text-sm text-gray-600 mt-1">
                                        {phaseInfo.description}
                                    </p>
                                </div>

                                <div className="pt-4 border-t border-gray-200">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-gray-600">Hourly Rate</span>
                                        <div className="flex items-center space-x-1">
                                            <DollarSign className={`w-5 h-5 text-${phaseInfo.color}-600`} />
                                            <span className={`text-xl font-bold text-${phaseInfo.color}-800`}>
                                                {profileData.hourlyRate}/hr
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 }}
                            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
                        >
                            <h3 className="font-bold text-gray-900 mb-4">Progression Path</h3>
                            
                            <div className="space-y-3">
                                {['shadow', 'trainee', 'junior', 'standard', 'experienced'].map((phase) => {
                                    const info = getPhaseInfo(phase);
                                    const isCurrent = phase === profileData.phase;
                                    
                                    return (
                                        <div
                                            key={phase}
                                            className={`p-3 rounded-lg ${
                                                isCurrent
                                                    ? `bg-${info.color}-50 border-2 border-${info.color}-300`
                                                    : 'bg-gray-50 border border-gray-200'
                                            }`}
                                        >
                                            <div className="flex items-center justify-between">
                                                <span className={`font-medium ${
                                                    isCurrent ? `text-${info.color}-800` : 'text-gray-700'
                                                }`}>
                                                    {info.name}
                                                </span>
                                                <span className={`text-sm font-semibold ${
                                                    isCurrent ? `text-${info.color}-600` : 'text-gray-500'
                                                }`}>
                                                    ${info.rate}/hr
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            <p className="text-xs text-gray-500 mt-4">
                                Your level is determined by your manager based on performance and experience.
                            </p>
                        </motion.div>
                    </div>
                </div>
            </div>
        </div>
    );
}
